/**
 * Field.tsx — `inputClass` plus a labelled form field. Wires the label
 * (`htmlFor`), hint/error ids via `aria-describedby`, and `aria-invalid` onto
 * the single child control (WCAG 1.3.1 / 3.3.1). Error text is prefixed with a
 * localised "Erro:" so it is not conveyed by colour alone.
 */

import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export const inputClass =
  'rounded-lg bg-surface-1 px-3 py-1.5 text-body text-ink-1 ring-1 ring-inset ring-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus disabled:opacity-60';

export interface FieldProps {
  id: string;
  label: string;
  /** Visually hide the label (still announced). */
  labelHidden?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export const Field = ({
  id,
  label,
  labelHidden = false,
  hint = undefined,
  error = undefined,
  children,
}: FieldProps) => {
  const { t } = useTranslation();
  const hintId = hint != null ? `${id}-hint` : undefined;
  const errorId = error != null ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className={`text-meta font-medium text-ink-2${labelHidden ? ' sr-only' : ''}`}
      >
        {label}
      </label>
      {isValidElement(children)
        ? cloneElement(children as ReactElement<Record<string, unknown>>, {
            'aria-describedby': describedBy,
            ...(error != null ? { 'aria-invalid': true } : {}),
          })
        : children}
      {hint != null ? (
        <p id={hintId} className="text-meta text-ink-3">
          {hint}
        </p>
      ) : null}
      {error != null ? (
        <p
          id={errorId}
          role="alert"
          data-testid={`${id}-error`}
          className="text-meta text-status-down"
        >
          <span className="font-medium">{t('form.errorPrefix')} </span>
          {error}
        </p>
      ) : null}
    </div>
  );
};
